/**
 * DeltaIndicator - Compact "+12%" / "-3" pill that renders the
 * difference between a current value and a comparison value. The
 * sign drives the color (green up, red down, gray flat) and the
 * arrow direction (▲ / ▼ / —).
 *
 * Two display modes:
 *   - mode='absolute' — renders the raw difference (+12, -3)
 *   - mode='percent'  — renders the percent change (+12%, -3%)
 *
 * The component does no formatting beyond sign + arrow + suffix.
 * Currency etc. is the parent's responsibility.
 *
 * @example
 * <DeltaIndicator current={120} previous={100} mode="percent" />
 * <DeltaIndicator current={42} previous={50} mode="absolute" suffix=" tasks" />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface DeltaIndicatorProps {
    /** The current period's value */
    current: number;
    /** The comparison period's value */
    previous: number;
    /** Display mode — absolute difference or percent change */
    mode?: 'absolute' | 'percent';
    /** Optional suffix to append to the value (e.g. " tasks") */
    suffix?: string;
    /** When true, an "up" delta is rendered red and "down" green
     *  (useful for metrics where lower is better, like outstanding
     *  invoices). Default is false (up=green). */
    invertColors?: boolean;
    /** Optional extra classes */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const DeltaIndicator: React.FC<DeltaIndicatorProps> = ({
    current,
    previous,
    mode = 'absolute',
    suffix,
    invertColors = false,
    className,
}) => {
    /* Compute the raw difference and the percent (guard against
     * divide-by-zero — when previous is 0 we emit "—" for percent
     * mode since the change is undefined). */
    const diff = current - previous;

    let display: string;
    if (mode === 'percent') {
        if (previous === 0) {
            display = '—';
        } else {
            const pct = (diff / Math.abs(previous)) * 100;
            const sign = pct > 0 ? '+' : pct < 0 ? '' : '';
            display = `${sign}${pct.toFixed(0)}%`;
        }
    } else {
        const sign = diff > 0 ? '+' : diff < 0 ? '' : '';
        display = `${sign}${diff}`;
    }
    if (suffix) display += suffix;

    /* Pick color/arrow based on direction + invert flag. */
    let trend: 'up' | 'down' | 'flat' = 'flat';
    if (diff > 0) trend = 'up';
    else if (diff < 0) trend = 'down';

    const colorClass = (() => {
        if (trend === 'flat') return 'text-brand-text-muted bg-brand-surface border-brand-border';
        const isGood = invertColors ? trend === 'down' : trend === 'up';
        return isGood
            ? 'text-green-700 bg-green-50 border-green-200'
            : 'text-red-700 bg-red-50 border-red-200';
    })();

    const arrow = trend === 'up' ? '▲' : trend === 'down' ? '▼' : '—';

    return (
        <span
            data-ref="atom-delta-indicator"
            className={[
                `delta-indicator delta-indicator--${trend}`,
                'inline-flex items-center gap-4px px-6px py-1px text-11px font-sans font-medium rounded border',
                colorClass,
                className ?? '',
            ].join(' ')}
            aria-label={`Change: ${display}`}
        >
            <span className="delta-indicator__arrow" aria-hidden="true">{arrow}</span>
            <span className="delta-indicator__value">{display}</span>
        </span>
    );
};

export default DeltaIndicator;
